import type { OutboxItem } from '../types';

export interface ContentDraft {
  target: string; subject: string; body: string; slug: string; title: string;
  summary: string; category: string; authorName: string; markdownBody: string;
  seoTitle: string; seoDescription: string; heroImageUrl: string;
  heroImageAlt: string; sourceUrls: string;
}

export interface ContentEditor {
  itemId: number;
  sourceSha256: string;
  baseline: ContentDraft;
  draft: ContentDraft;
}

export function createContentEditor(item: OutboxItem): ContentEditor {
  const publication = item.publication;
  const draft: ContentDraft = {
    target: item.target, subject: item.subject, body: item.body,
    slug: publication?.slug || '', title: publication?.title || '',
    summary: publication?.summary || '', category: publication?.category || 'company-news',
    authorName: publication?.authorName || '', markdownBody: publication?.markdownBody || '',
    seoTitle: publication?.seoTitle || '', seoDescription: publication?.seoDescription || '',
    heroImageUrl: publication?.heroImageUrl || '', heroImageAlt: publication?.heroImageAlt || '',
    sourceUrls: publication?.sourceUrls.join('\n') || ''
  };
  return { itemId: item.id, sourceSha256: item.payloadSha256, baseline: draft, draft };
}

export function contentEditorIsDirty(editor: ContentEditor | null): boolean {
  return !!editor && (Object.keys(editor.draft) as Array<keyof ContentDraft>)
    .some(key => editor.draft[key] !== editor.baseline[key]);
}

/** Background reads must never replace an unsaved draft, including after Back navigation. */
export function reconcileContentEditor(editor: ContentEditor | null | undefined, item: OutboxItem): ContentEditor {
  return editor?.itemId === item.id && contentEditorIsDirty(editor) ? editor : createContentEditor(item);
}

export function contentEditorMatchesSaved(editor: ContentEditor | null, item: OutboxItem | null): boolean {
  return !!editor && !!item && editor.itemId === item.id
    && editor.sourceSha256 === item.payloadSha256 && !contentEditorIsDirty(editor);
}

export function contentEditorPayload(editor: ContentEditor, item: OutboxItem): Record<string, unknown> {
  const draft = editor.draft;
  return {
    target: draft.target, subject: draft.subject, body: draft.body,
    expected_payload_sha256: editor.sourceSha256,
    publication: item.publication ? {
      slug: draft.slug, title: draft.title, excerpt: draft.summary, category: draft.category,
      author_name: draft.authorName, markdown_body: draft.markdownBody,
      seo_title: draft.seoTitle, seo_description: draft.seoDescription,
      hero_image_url: draft.heroImageUrl || null, hero_image_alt: draft.heroImageAlt || null,
      source_urls: draft.sourceUrls.split(/\r?\n/).map(value => value.trim()).filter(Boolean)
    } : null
  };
}

export function leavesContentLocation(href: string, current: string): boolean {
  const destination = new URL(href, current), location = new URL(current);
  return destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search;
}
